import type {
  RecentlyViewedMonarchApiValue,
  RecentlyViewedMonarchData,
  RecentlyViewedSurface,
} from "./types";

/** PRD defaults when Monarch omits a field (see EDUN-10032 / RV2U PRD configurability table). */
export const RECENTLY_VIEWED_PRD_DEFAULTS = {
  homepageEnabled: true,
  pdpEnabled: true,
  homepageMaxRows: 2 as const,
  homepageMinViews: 3,
  pdpMaxProducts: 4,
  pdpMinViews: 2,
  recencyDays: 14 as const,
};

/** Optional Monarch tunables merged over CMS `callProps` (bounds enforced in Monarch). */
export type RecentlyViewedMonarchPartial = {
  homepageEnabled?: boolean;
  pdpEnabled?: boolean;
  homepageMaxRows?: number;
  homepageMinViews?: number;
  recencyDays?: number;
  moduleTitle?: string;
  pdpMaxProducts?: number;
  pdpMinViews?: number;
};

function looksLikeTunables(layer: Record<string, unknown>): boolean {
  return (
    "homepageEnabled" in layer ||
    "pdpEnabled" in layer ||
    "moduleTitle" in layer ||
    "homepageMaxRows" in layer ||
    "recencyDays" in layer ||
    "homepageMinViews" in layer ||
    "homepageMinItemsToRender" in layer ||
    "pdpMaxProducts" in layer ||
    "pdpMinViews" in layer ||
    "pdpMinItemsToRender" in layer
  );
}

/**
 * Cohesion returns a **variant** object: `value.data` is `{ enabled, data: tunables }`.
 * Some paths use a flat `{ enable?, data?: tunables }` shape (see `homepageTrendingCourseUpdate`).
 */
export function unwrapRecentlyViewedMonarchValue(
  value: unknown,
): RecentlyViewedMonarchApiValue | undefined {
  if (value == null || typeof value !== "object") {
    return undefined;
  }
  const v = value as Record<string, unknown>;

  const outerData = v.data;
  if (outerData && typeof outerData === "object") {
    const layer = outerData as Record<string, unknown>;
    if (layer.data != null && typeof layer.data === "object") {
      return {
        enable: typeof layer.enabled === "boolean" ? layer.enabled : undefined,
        data: layer.data as RecentlyViewedMonarchData,
      };
    }
    if (looksLikeTunables(layer)) {
      return {
        enable: typeof v.enable === "boolean" ? v.enable : undefined,
        data: layer as RecentlyViewedMonarchData,
      };
    }
  }

  if (looksLikeTunables(v)) {
    return {
      enable: typeof v.enable === "boolean" ? v.enable : undefined,
      data: v as unknown as RecentlyViewedMonarchData,
    };
  }

  return {
    enable: typeof v.enable === "boolean" ? v.enable : undefined,
    data: undefined,
  };
}

/** True when Monarch returned an explicit enable flag or tunable payload. */
export function isRecentlyViewedMonarchResolved(value: unknown): boolean {
  if (value == null) {
    return false;
  }

  const monarch = unwrapRecentlyViewedMonarchValue(value);
  if (!monarch) {
    return false;
  }

  if (typeof monarch.enable === "boolean") {
    return true;
  }

  return monarch.data != null;
}

/**
 * Maps raw Monarch `data` to optional overrides when each field is present.
 * Tunable ranges are enforced in Monarch; this layer only normalizes shape (e.g. min-items aliases).
 */
export function pickRecentlyViewedMonarchOverrides(
  raw?: RecentlyViewedMonarchData,
): RecentlyViewedMonarchPartial {
  if (!raw) {
    return {};
  }

  const partial: RecentlyViewedMonarchPartial = {};

  if (raw.homepageEnabled !== undefined) {
    partial.homepageEnabled = raw.homepageEnabled;
  }
  if (raw.pdpEnabled !== undefined) {
    partial.pdpEnabled = raw.pdpEnabled;
  }
  if (raw.homepageMaxRows !== undefined) {
    partial.homepageMaxRows = raw.homepageMaxRows;
  }

  const homepageMin = raw.homepageMinViews ?? raw.homepageMinItemsToRender;
  if (homepageMin !== undefined) {
    partial.homepageMinViews = homepageMin;
  }

  if (raw.recencyDays !== undefined) {
    partial.recencyDays = raw.recencyDays;
  }

  if (typeof raw.moduleTitle === "string") {
    partial.moduleTitle = raw.moduleTitle.trim();
  }

  if (raw.pdpMaxProducts !== undefined) {
    partial.pdpMaxProducts = raw.pdpMaxProducts;
  }

  const pdpMin = raw.pdpMinViews ?? raw.pdpMinItemsToRender;
  if (pdpMin !== undefined) {
    partial.pdpMinViews = pdpMin;
  }

  return partial;
}

export function isRecentlyViewedGloballyDisabled(
  api: RecentlyViewedMonarchApiValue | null | undefined,
): boolean {
  return api?.enable === false;
}

/** When a flag is omitted in `partial`, PRD default applies for enable checks. */
export function isRecentlyViewedSurfaceEnabled(
  surface: RecentlyViewedSurface,
  partial: RecentlyViewedMonarchPartial,
  api: RecentlyViewedMonarchApiValue | null | undefined,
): boolean {
  if (isRecentlyViewedGloballyDisabled(api)) {
    return false;
  }
  if (surface === "homepage") {
    const enabled =
      partial.homepageEnabled ?? RECENTLY_VIEWED_PRD_DEFAULTS.homepageEnabled;
    return enabled !== false;
  }
  const enabled = partial.pdpEnabled ?? RECENTLY_VIEWED_PRD_DEFAULTS.pdpEnabled;
  return enabled !== false;
}

export type RecentlyViewedResolvedDisplay = {
  minItemsToRender: number;
  maxItems: number;
  displayLimit?: number;
  recencyDays: number;
  title: string;
  maxRows: number;
  initialCardsLength: number;
};

type CallPropsInput = {
  minItemsToRender: number;
  maxItems: number;
  displayLimit?: number;
  recencyDays: number;
  title: string;
  maxRows: number;
  initialCardsLength: number;
};

/**
 * Monarch > CMS/call props > component defaults (callProps already include destructured defaults).
 */
export function buildRecentlyViewedResolvedDisplay(args: {
  surface: RecentlyViewedSurface;
  partial: RecentlyViewedMonarchPartial;
  callProps: CallPropsInput;
}): RecentlyViewedResolvedDisplay {
  const { surface, partial, callProps } = args;

  const recencyDays =
    partial.recencyDays !== undefined
      ? partial.recencyDays
      : callProps.recencyDays;

  const title =
    partial.moduleTitle !== undefined && partial.moduleTitle.length > 0
      ? partial.moduleTitle
      : callProps.title;

  if (surface === "homepage") {
    const minItemsToRender =
      partial.homepageMinViews !== undefined
        ? partial.homepageMinViews
        : callProps.minItemsToRender;
    const maxRows =
      partial.homepageMaxRows !== undefined
        ? partial.homepageMaxRows
        : callProps.maxRows;
    const maxItems = Math.max(callProps.maxItems, maxRows * 4 + 2);
    return {
      minItemsToRender,
      maxItems,
      displayLimit: callProps.displayLimit,
      recencyDays,
      title,
      maxRows,
      initialCardsLength: callProps.initialCardsLength,
    };
  }

  const minItemsToRender =
    partial.pdpMinViews !== undefined
      ? partial.pdpMinViews
      : callProps.minItemsToRender;
  const displayLimit =
    partial.pdpMaxProducts !== undefined
      ? partial.pdpMaxProducts
      : (callProps.displayLimit ?? RECENTLY_VIEWED_PRD_DEFAULTS.pdpMaxProducts);
  const maxItems = Math.max(callProps.maxItems, displayLimit + 2);

  return {
    minItemsToRender,
    maxItems,
    displayLimit,
    recencyDays,
    title,
    maxRows: callProps.maxRows,
    initialCardsLength: displayLimit,
  };
}
